import classNames from 'classnames'
import Link from 'next/link'

import CornerBackgroundImage from '@/components/Combo/CornerBackgroundImage/CornerBackgroundImage'
import BracketBlock from '@/components/Combo/MetaData/BracketBlock/BracketBlock'
import Icon from '@/components/Icon/Icon'
import { RecCardType } from '@/types/card'
import { ComboData } from '@/types/page'
import { comboTierState } from '@/utils/combos'
import { artCrop } from '@/utils/images'

import styles from './MetaData.module.scss'

type Props = {
  cards: RecCardType[]
  combo: ComboData
}

const BracketInfo = ({ cards, combo }: Props) => {
  const { lowTierMessage, lowTierState, midTierMessage, midTierState } = comboTierState(combo, cards)

  return (
    <div className={styles.infoContainer}>
      <CornerBackgroundImage
        image={artCrop(combo.cardIds[2] ? combo.cardIds[2] : '14019383-0327-404b-ab4d-c65c3fa8c50d')}
        position='topRight'
        size={300}
      />
      <h2 className={styles.header} id='bracket-info'>
        <b>&#123;&nbsp;&#125;</b>&nbsp;&nbsp;Commander Bracket Information
      </h2>
      <h5 className={styles.subHeader}>In which Commander Bracket(s) am I allowed to play this combo?</h5>
      <div className={styles.statContainer}>
        <BracketBlock
          description={lowTierMessage}
          label='1-2'
          state={lowTierState}
          subtext='(no 2-card combos)'
          title='Exhibition and Core'
        />
        <BracketBlock
          description={midTierMessage}
          label='3'
          state={midTierState}
          subtext='(no early game 2-card combos)'
          title='Upgraded'
        />
        <BracketBlock
          description='All combos are allowed.'
          label='4-5'
          state='success'
          subtext='(all combos are allowed)'
          title='Optimized and CEDH'
        />
      </div>
      {combo.comboVote && (
        <>
          <h2 className={styles.header}>
            <Icon icon='handshake' />
            &nbsp;&nbsp;Combo Voting
          </h2>

          <div className='fullWidth d-flex justify-content-center'>
            <p className={styles.subtext}>
              Does the community think this a true 2-card combo (<i>both</i> trivial prerequisites and significant
              results)? Do they think it can be executed early game?
            </p>
          </div>

          <div className={styles.chartsWrapper}>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Trivial Prerequisites:</label>
              <div className={styles.baseBar}>
                <div
                  className={classNames(
                    styles.bar,
                    combo.comboVote.prereqScore >= 0.4 && combo.comboVote.prereqScore < 0.5 && styles.contestedBar,
                    combo.comboVote.prereqScore >= 0.5 && styles.activeBar,
                  )}
                  style={{ width: `${combo.comboVote.prereqScore * 100}%` }}
                />
              </div>
              <div className={styles.barPercent}>{Math.round(combo.comboVote.prereqScore * 100)}%</div>
            </div>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Significant Results:</label>
              <div className={styles.baseBar}>
                <div
                  className={classNames(
                    styles.bar,
                    combo.comboVote.resultScore >= 0.4 && combo.comboVote.resultScore < 0.5 && styles.contestedBar,
                    combo.comboVote.resultScore >= 0.5 && styles.activeBar,
                  )}
                  style={{ width: `${combo.comboVote.resultScore * 100}%` }}
                />
              </div>
              <div className={styles.barPercent}>{Math.round(combo.comboVote.resultScore * 100)}%</div>
            </div>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Early Game:</label>
              <div className={styles.baseBar}>
                <div
                  className={classNames(
                    styles.bar,
                    combo.comboVote.earlyScore >= 0.4 && combo.comboVote.earlyScore < 0.5 && styles.contestedBar,
                    combo.comboVote.earlyScore >= 0.5 && styles.activeBar,
                  )}
                  style={{ width: `${combo.comboVote.earlyScore * 100}%` }}
                />
              </div>
              <div className={styles.barPercent}>{Math.round(combo.comboVote.earlyScore * 100)}%</div>
            </div>
          </div>
          <div className='d-flex justify-content-end'>
            <Link href={`/articles/wotc-introduces-new-bracket-system-for-edh?utm_source=edhrec`}>
              Learn more about the Bracket system here...
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

export default BracketInfo
